import Vue from 'vue'
import Router from 'vue-router'
import { store } from '@/store/store'
import i18n from './i18n/i18n.js'
Vue.use(Router)

const fromRouter = (from, next, path) => {
  if (from.path === '/') {
    next(path)
  } else {
    next(from.path)
  }
}

const hiveData = (to, from, next, type) => {
  return store.dispatch('getHive', { id: to.params.id }).then((response) => {
    const user = store.getters.returnUserInfo
    const role = store.getters.userRole

    if (user) {
      if (response.data.user.id === user.user_id) {
        next()
      } else if (response.data.user.id !== user.user_id) {
        if (role === 'is_super' && (type === 'view' || type === 'chart')) {
          next()
        } else {
          fromRouter(from, next, '/hives')
        }
      }
    } else if (!user) {
      fromRouter(from, next, '/hives')
    }
  }).catch(() => {
    fromRouter(from, next, '/hives')
  })
}

const apiaryData = (to, from, next, type) => {
  return store.dispatch('getApiary', { id: to.params.id }).then((response) => {
    const user = store.getters.returnUserInfo
    const role = store.getters.userRole

    if (user) {
      if (response.data.user === user.user_id) {
        next()
      } else if (response.data.user !== user.user_id) {
        if (role === 'is_super' && type === 'view') {
          next()
        } else {
          fromRouter(from, next, '/apiaries')
        }
      }
    } else if (!user) {
      fromRouter(from, next, '/apiaries')
    }
  }).catch(() => {
    fromRouter(from, next, '/apiaries')
  })
}

const noteData = (to, from, next) => {
  return store.dispatch('getHiveNoteData', to.params.id).then((response) => {
    const user = store.getters.returnUserInfo
    if (user) {
      if (response.data.user.id === user.user_id) {
        next()
      } else if (response.data.user !== user.user_id) {
        fromRouter(from, next, '/notes')
      }
    } else if (!user) {
      fromRouter(from, next, '/notes')
    }
  }).catch(() => {
    fromRouter(from, next, '/notes')
  })
}

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior () {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: '',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================

        {
          path: '/',
          redirect: '/hives'
        },
        {
          path: '/apiaries',
          component: () => import('./views/apiaries/index.vue'),
          name: 'apiaries',
          meta: {
            requiresAuth: true,
            isReadNotification: true,
            rule: 'editor',
            pageTitle: i18n.t('sidebar.Apiaries')
          }
        },
        {
          path: '/apiaries/apiary/:id',
          component: () => import('./views/apiaries/detail/index.vue'),
          name: 'apiary-detail',
          meta: {
            requiresAuth: true,
            isReadNotification: false,
            rule: 'editor',
            pageTitle: i18n.t('apiaries.ApiaryDetail')
          },
          beforeEnter: (to, from, next) => {
            apiaryData(to, from, next, 'view')
          }
        },
        {
          path: '/apiaries/new',
          component: () => import('./views/apiaries/new/index.vue'),
          name: 'new-apiary',
          meta: {
            requiresAuth: true,
            isReadNotification: false,
            rule: 'editor',
            pageTitle: i18n.t('apiaries.AddApiary')
          }
        },
        {
          path: '/apiaries/apiary/edit/:id',
          component: () => import('./views/apiaries/edit/index.vue'),
          name: 'edit-apiary',
          meta: {
            requiresAuth: true,
            isReadNotification: false,
            rule: 'editor',
            pageTitle: i18n.t('apiaries.edit.EditApiaries')
          },
          beforeEnter: (to, from, next) => {
            apiaryData(to, from, next, 'edit')
          }
        },
        {
          path: '/apiaries/apiary/delete/:id',
          component: () => import('./views/apiaries/delete/index.vue'),
          name: 'delete-apiary',
          meta: {
            requiresAuth: true,
            isReadNotification: false,
            rule: 'editor',
            pageTitle: i18n.t('apiaries.delete.DeleteApiary')
          },
          beforeEnter: (to, from, next) => {
            apiaryData(to, from, next, 'delete')
          }
        },
        {
          path: '/hives',
          component: () => import('./views/hives/index.vue'),
          name: 'hives',
          meta: {
            requiresAuth: true,
            isReadNotification: true,
            rule: 'editor',
            pageTitle: i18n.t('sidebar.Hives')
          }
        },
        {
          path: '/hives/new',
          component: () => import('./views/hives/new/index.vue'),
          name: 'new-hive',
          meta: {
            requiresAuth: true,
            isReadNotification: false,
            rule: 'editor',
            pageTitle: i18n.t('hives.CreateNewHive')
          }
        },
        {
          path: '/hives/hive/delete/:id',
          component: () => import('./views/hives/delete/index.vue'),
          name: 'delete-hives',
          meta: {
            requiresAuth: true,
            isReadNotification: false,
            rule: 'editor',
            pageTitle: i18n.t('hives.delete.Title')
          },
          beforeEnter: (to, from, next) => {
            hiveData(to, from, next, 'delete')
          }
        },
        {
          path: '/hives/hive/edit/:id',
          component: () => import('./views/hives/edit/index.vue'),
          name: 'edit-hives',
          meta: {
            requiresAuth: true,
            isReadNotification: false,
            rule: 'editor',
            pageTitle: i18n.t('hives.edit.EditHives')
          },
          beforeEnter: (to, from, next) => {
            hiveData(to, from, next, 'edit')
          }
        },
        {
          path: '/hives/hive/charts/:id',
          component: () => import('./views/hives/detail/charts/index'),
          name: 'hive-charts',
          meta: {
            requiresAuth: true,
            isReadNotification: false,
            rule: 'editor',
            pageTitle: i18n.t('hives.HiveDetail')
          },
          beforeEnter: (to, from, next) => {
            hiveData(to, from, next, 'chart')
          }
        },
        {
          path: '/hives/hive/:id',
          component: () => import('./views/hives/detail/index'),
          name: 'show-hives',
          meta: {
            requiresAuth: true,
            isReadNotification: false,
            rule: 'editor',
            pageTitle: i18n.t('hives.HiveDetail')
          },
          beforeEnter: (to, from, next) => {
            hiveData(to, from, next, 'view')
          }
        },
        {
          path: '/devices/new',
          component: () => import('./views/devices/new/index.vue'),
          name: 'new-device',
          meta: {
            requiresAuth: true,
            isReadNotification: false,
            rule: 'editor',
            pageTitle: i18n.t('hives.AddDevice')
          }
        },
        {
          path: '/hives/notes',
          component: () => import('./views/notes/index.vue'),
          name: 'hive-note',
          meta: {
            requiresAuth: true,
            isReadNotification: false,
            rule: 'editor',
            pageTitle: i18n.t('hivenote.Title')
          }
        },
        {
          path: '/hives/notes/new',
          component: () => import('./views/notes/new/index.vue'),
          name: 'new-hive-note',
          meta: {
            requiresAuth: true,
            isReadNotification: false,
            rule: 'editor',
            pageTitle: i18n.t('hivenote.AddNewNote')
          }
        },
        {
          path: '/hives/notes/note/edit/:id',
          component: () => import('./views/notes/edit/index.vue'),
          name: 'edit-hive-note',
          meta: {
            requiresAuth: true,
            isReadNotification: false,
            rule: 'editor',
            pageTitle: i18n.t('hivenote.edit.Title')
          },
          beforeEnter: (to, from, next) => {
            noteData(to, from, next)
          }
        },
        {
          path: '/alarms',
          component: () => import('./views/alarms/index.vue'),
          name: 'alarms',
          meta: {
            requiresAuth: true,
            isReadNotification: true,
            rule: 'editor',
            pageTitle: i18n.t('sidebar.Warnings')
          }
        },
        {
          path: '/rules',
          component: () => import('./views/rules/index.vue'),
          name: 'rules',
          meta: {
            requiresAuth: true,
            isReadNotification: true,
            rule: 'editor',
            pageTitle: i18n.t('sidebar.Rules')
          }
        },
        {
          path: '/rules/new',
          component: () => import('./views/rules/new/index.vue'),
          name: 'new-rule',
          meta: {
            requiresAuth: true,
            isReadNotification: false,
            rule: 'editor',
            pageTitle: i18n.t('rules.main.AddRule')
          }
        },
        {
          path: '/rules/rule/edit/:id',
          component: () => import('./views/rules/edit/index.vue'),
          name: 'edit-rule',
          meta: {
            requiresAuth: true,
            isReadNotification: false,
            rule: 'editor',
            pageTitle: i18n.t('breadcrumbs.EditRule')
          }
        },
        {
          path: '/rules/rule/delete/:id',
          component: () => import('./views/rules/delete/index.vue'),
          name: 'delete-rule',
          meta: {
            requiresAuth: true,
            isReadNotification: false,
            rule: 'editor',
            pageTitle: i18n.t('rules.delete.Title')
          }
        },
        {
          path: '/admin',
          component: () => import('./views/admin/index.vue'),
          meta: {
            requiresAuth: true,
            isReadNotification: false,
            rule: 'admin',
            pageTitle: i18n.t('admin.Title')
          },
          children: [
            {
              path: '',
              component: () => import('./views/admin/users/index.vue'),
              name: 'admin-user',
              meta: {
                requiresAuth: true,
                isReadNotification: false,
                rule: 'admin',
                pageTitle: i18n.t('admin.Title')
              }
            },
            {
              path: 'devices',
              component: () => import('./views/admin/devices/index.vue'),
              name: 'admin-device',
              meta: {
                requiresAuth: true,
                isReadNotification: false,
                rule: 'admin',
                pageTitle: i18n.t('admin.Title')
              }
            }
          ]
        },
        {
          path: '/admin/devices/edit/:id',
          component: () => import('./views/admin/devices/edit/index.vue'),
          name: 'admin-device-edit',
          meta: {
            requiresAuth: true,
            isReadNotification: false,
            rule: 'admin',
            pageTitle: i18n.t('admin.devices.edit.Title')
          }
        },
        {
          path: '/admin/devices/rental/new',
          component: () => import('./views/admin/devices/rental/new/index.vue'),
          name: 'new-rental',
          meta: {
            requiresAuth: true,
            isReadNotification: false,
            rule: 'admin',
            pageTitle: i18n.t('admin.devices.new.Title')
          }
        },
        {
          path: '/profile',
          component: () => import('./views/profile/index.vue'),
          meta: {
            requiresAuth: true,
            isReadNotification: false,
            rule: 'editor',
            pageTitle: i18n.t('profile.Title')
          },
          children: [
            {
              path: '',
              component: () => import('./views/profile/account-settings.vue'),
              name: 'profile-acount-settings',
              meta: {
                requiresAuth: true,
                isReadNotification: false,
                rule: 'editor',
                pageTitle: i18n.t('profile.Title'),
                tab: 1
              },
              children: [
                {
                  path: 'user-settings',
                  component: () => import('./views/profile/account-settings.vue'),
                  name: 'user-settings',
                  meta: {
                    requiresAuth: true,
                    isReadNotification: false,
                    rule: 'editor',
                    pageTitle: i18n.t('profile.Title'),
                    set_tab: 1,
                    tab: 1
                  }
                },
                {
                  path: 'sms-settings',
                  component: () => import('./views/profile/account-settings.vue'),
                  name: 'sms-settings',
                  meta: {
                    requiresAuth: true,
                    isReadNotification: false,
                    rule: 'editor',
                    pageTitle: i18n.t('profile.Title'),
                    set_tab: 2,
                    tab: 1
                  }
                },
                {
                  path: 'languages-settings',
                  component: () => import('./views/profile/account-settings.vue'),
                  name: 'languages-settings',
                  meta: {
                    requiresAuth: true,
                    isReadNotification: false,
                    rule: 'editor',
                    pageTitle: i18n.t('profile.Title'),
                    set_tab: 3,
                    tab: 1
                  }
                }
              ]
            },
            {
              path: 'change-password',
              component: () => import('./views/profile/password-change.vue'),
              name: 'profile-change-password',
              meta: {
                requiresAuth: true,
                isReadNotification: false,
                rule: 'editor',
                pageTitle: i18n.t('profile.Title'),
                tab: 2
              }
            },
            {
              path: 'device-rentals',
              component: () => import('./views/profile/device-rentals.vue'),
              meta: {
                requiresAuth: true,
                isReadNotification: false,
                rule: 'editor',
                pageTitle: i18n.t('profile.Title'),
                tab: 3
              },
              children: [
                {
                  path: '',
                  component: () => import('./views/profile/device-rentals.vue'),
                  name: 'active-leases',
                  meta: {
                    requiresAuth: true,
                    isReadNotification: false,
                    rule: 'editor',
                    pageTitle: i18n.t('profile.Title'),
                    rent_tab: 1,
                    tab: 3
                  }
                },
                {
                  path: 'expiring-leases',
                  component: () => import('./views/profile/device-rentals.vue'),
                  name: 'expiring-leases',
                  meta: {
                    requiresAuth: true,
                    isReadNotification: false,
                    rule: 'editor',
                    pageTitle: i18n.t('profile.Title'),
                    rent_tab: 2,
                    tab: 3
                  }
                },
                {
                  path: 'expired-leases',
                  component: () => import('./views/profile/device-rentals.vue'),
                  name: 'expired-leases',
                  meta: {
                    requiresAuth: true,
                    isReadNotification: false,
                    rule: 'editor',
                    pageTitle: i18n.t('profile.Title'),
                    rent_tab: 3,
                    tab: 3
                  }
                }
              ]
            }
          ]
        },
        {
          path: '/help',
          component: () => import('./views/help/index.vue'),
          name: 'help',
          meta: {
            requiresAuth: true,
            isReadNotification: false,
            rule: 'editor',
            pageTitle: i18n.t('help.Title')
          }
        }
      ]
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: '/login',
          name: 'page-login',
          component: () => import('@/views/auth/login.vue'),
          meta: {
            requiresAuth: false,
            isReadNotification: false,
            rule: 'editor'
          }
        },
        {
          path: '/register',
          name: 'page-register',
          component: () => import('@/views/auth/register.vue'),
          meta: {
            requiresAuth: false,
            isReadNotification: false,
            rule: 'editor'
          }
        },
        {
          path: '/forgot-password',
          name: 'page-forgot-password',
          component: () => import('@/views/auth/forgot-password.vue'),
          meta: {
            requiresAuth: false,
            isReadNotification: false,
            rule: 'editor'
          }
        },
        {
          path: '/reset-password/:token',
          name: 'page-reset-password',
          component: () => import('@/views/auth/reset-password.vue'),
          meta: {
            requiresAuth: false,
            isReadNotification: false,
            rule: 'editor'
          }
        },
        {
          path: '/activate/:id',
          name: 'page-activate',
          component: () => import('@/views/auth/activated.vue'),
          meta: {
            requiresAuth: false,
            isReadNotification: false,
            rule: 'editor'
          }
        },
        {
          path: '/not-found',
          name: '404',
          component: () => import('@/views/pages/404/index.vue'),
          meta: {
            requiresAuth: !!localStorage.getItem('token'),
            isReadNotification: false,
            rule: 'editor'
          }
        },
        {
          path: '/under-maintenance',
          name: 'under-maintenance',
          component: () => import('@/views/pages/maintenance/index.vue'),
          meta: {
            requiresAuth: !!localStorage.getItem('token'),
            isReadNotification: false,
            rule: 'editor'
          }
        },
        // =============================================================================
        // POLICIES
        // =============================================================================
        {
          path: '/user-agreement',
          name: 'user-agreement',
          component: () => import('@/views/policy/user-agreement.vue'),
          meta: {
            requiresAuth: !!localStorage.getItem('token'),
            isReadNotification: false,
            rule: 'editor'
          }
        },
        {
          path: '/privacy-policy',
          name: 'privacy-policy',
          component: () => import('@/views/policy/privacy-policy.vue'),
          meta: {
            requiresAuth: !!localStorage.getItem('token'),
            isReadNotification: false,
            rule: 'editor'
          }
        },
        {
          path: '/cookie-policy',
          name: 'cookie-policy',
          component: () => import('@/views/policy/cookie-policy.vue'),
          meta: {
            requiresAuth: !!localStorage.getItem('token'),
            isReadNotification: false,
            rule: 'editor'
          }
        },
        {
          path: '/auth/help',
          component: () => import('./views/help/index.vue'),
          name: 'auth-help',
          meta: {
            requiresAuth: !!localStorage.getItem('token'),
            isReadNotification: false,
            rule: 'editor'
          }
        },
        // =============================================================================
        // SUPPORT
        // =============================================================================
        {
          path: 'support',
          component: () => import('./views/contact-form/index.vue'),
          name: 'support',
          meta: {
            requiresAuth: !!localStorage.getItem('token'),
            rule: 'editor',
            isReadNotification: false
          }
        }
      ]
    },
    // Redirect to 404 page, if no match found
    {
      path: '*',
      redirect: '/not-found',
      meta: {
        rule: 'editor',
        isReadNotification: false,
        requiresAuth: !!localStorage.getItem('token')
      }
    }
  ]
})


router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
})

router.beforeEach((to, from, next) => {
  const loggedIn = localStorage.getItem('token')
  if (to.matched.some(record => record.meta.requiresAuth) && !loggedIn) {
    return next('/login')
  } else if (!to.matched.some(record => record.meta.requiresAuth) && loggedIn) {
    return next('/hives')
  }
  if (to.matched.some(record => record.meta.isReadNotification)) {
    store.dispatch('getNotifications').catch(() => { })
    store.dispatch('getNotificationsCount').catch(() => { })
  }
  next()
})

export default router
